import { FormikProps, useFormikContext, withFormik } from 'formik'
import { FieldValidator, FormikErrors } from 'formik/dist/types'
import { t } from 'i18next'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'
import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { FileData } from '@ui/components/FileUpload/types'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { CommonPaths } from '@utils/path'
import { isDoubleSidedDocument } from '@utils/uploadDocument'

import { NameDto } from '@model/NameDto'
import { UploadDocumentTypeEnum } from '@model/UploadDocumentTypeEnum'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'

import { DocumentFileUploadsFactory } from '../DocumentFileUploadsFactory'
import { useDocumentText } from './useDocumentText'

const FormCheckBoxField = createFormCheckBoxField<DocumentManagementNonNCIFormValues>()

export interface DocumentManagementNonNCIFormValues {
  documentCategory: NameDto
  documentCategoryType: NameDto
  documents: DocumentManagementDocument[]
  terms: boolean
}

interface DocumentManagementDocument {
  categoryId: number
  typeId: number
  file: string
  filename: string
  error: boolean
}

export const DocumentManagementNonNCIFormUI: React.FC<
  FormikProps<DocumentManagementNonNCIFormValues> & OuterProps
> = (props) => {
  useScrollToTop()
  const { documentTitle, documentCategoryTypeName, showTerms, documentId } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const cancelModal = useModal(ActionModal, {
    title: t('Cancel Adding Document'),
    description: t('Do you really like to cancel document adding process'),
    onConfirm: () => navigate(CommonPaths.DocumentManagement),
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    props.handleSubmit()
  }

  return (
    <FormTemplate title={t('Sign up.Add Document')} goBack={cancelModal.open}>
      <Form onSubmit={handleSubmit}>
        <FormFields
          documentId={documentId}
          documentTitle={documentTitle}
          documentCategoryTypeName={documentCategoryTypeName}
          showTerms={showTerms}
          onCancel={cancelModal.open}
        />
      </Form>
    </FormTemplate>
  )
}

export interface DocumentManagementNonNCIFormFieldsFactoryProps {
  documentId?: UploadDocumentTypeEnum
  documentTitle: string
  documentCategoryTypeName: string
  showTerms: boolean
  onCancel(): void
}

const FormFields: React.FC<DocumentManagementNonNCIFormFieldsFactoryProps> = (props) => {
  const { documentTitle, showTerms, onCancel, documentCategoryTypeName } = props
  const { t } = useTranslation()
  const { values, isValid, setValues } = useFormikContext<DocumentManagementNonNCIFormValues>()
  const documentCategoryDescription = useDocumentText(props)
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
  }, [])

  const handleDocumentUploads = (fileData: FileData[]) => {
    setValues({
      ...values,
      documents: fileData.map((x) => ({
        ...x,
        categoryId: values.documentCategory.id,
        typeId: values.documentCategoryType.id,
        filename: x.fileName,
        file: x.base64Content,
        error: x.error ?? false,
        errorMessage: x.errorMessage ?? '',
      })),
    })
  }

  return (
    <Loading isLoading={!props.documentId} showLoadingIcon>
      <div className='pb-2'>
        <Body1 medium>{documentTitle}</Body1>
        <Body1 isParagraph>{documentCategoryTypeName}</Body1>
      </div>
      <div className='pb-5'>
        <div className='pb-3'>{documentCategoryDescription}</div>
        <div>
          <div className='pb-3'>
            <DocumentFileUploadsFactory
              buttonText={t('Profile.Upload File')}
              categorySizes={categorySizes}
              documentTypeId={values.documentCategoryType.id}
              documentCategory={values.documentCategory.id}
              onChange={handleDocumentUploads}
            />
          </div>
        </div>
      </div>
      {showTerms && (
        <div className='pb-5'>
          <FormCheckBoxField
            name='terms'
            label={t('Profile.I confirm that document has more than 3 months of validity.')}
            validate={(value: FieldValidator) => {
              if (!value) {
                return t('Validation.Required')
              }
              return undefined
            }}
          />
        </div>
      )}
      <TransactionFormActionButtonTemplate>
        <Button.Secondary size='lg' onClick={onCancel}>
          {t('Cancel')}
        </Button.Secondary>
        <Button.Primary size='lg' type='submit' disabled={!isValid}>
          {t('Confirm')}
        </Button.Primary>
      </TransactionFormActionButtonTemplate>
    </Loading>
  )
}

interface OuterProps {
  documentTitle: string
  documentCategoryTypeName: string
  documentId?: UploadDocumentTypeEnum
  documentCategory: NameDto
  documentCategoryType: NameDto
  showTerms: boolean
  onSubmit(values: DocumentManagementNonNCIFormValues): void
}

interface StateProps {
  documentCategory: NameDto
  documentCategoryType: NameDto
}

const initialState = (props: StateProps): DocumentManagementNonNCIFormValues => {
  const { documentCategory, documentCategoryType } = props || {}

  return {
    documentCategory: {
      id: documentCategory.id,
      name: documentCategory.name,
    },
    documentCategoryType: {
      id: documentCategoryType.id,
      name: documentCategoryType.name,
    },
    documents: [],
    terms: false,
  }
}

export const DocumentManagementNonNCIForm = withFormik<
  OuterProps,
  DocumentManagementNonNCIFormValues
>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(values)
    } catch (e: unknown) {
      console.error(e)
      throw e
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<DocumentManagementNonNCIFormValues> = {}

    const documents = values.documents.filter((x) => !!x?.filename).filter((x) => !x.error)

    const doubleSidedDocumentSize = isDoubleSidedDocument(
      props.documentCategory.id,
      values.documentCategoryType.id
    )
      ? 1
      : 0

    if (documents.length <= doubleSidedDocumentSize) {
      errors.documents = t('Validation.Required')
    }

    if (values.documents.some((doc) => doc.error)) {
      errors.documents = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
  validateOnMount: true,
})(DocumentManagementNonNCIFormUI)
